import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTheme} from '../../theme';
import {getPopupPosition} from './functions';
import {PopupProps} from './types';

export const Popup: React.FC<PopupProps> = ({
  trigger,
  absolute,
  flex,
  children,
}) => {
  const theme = useTheme();

  // State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [triggerRect, setTriggerRect] = useState<
    undefined | Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>
  >(undefined);

  const [contentRect, setContentRect] = useState<
    undefined | Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>
  >(undefined);

  // Ref for trigger and menu
  const triggerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handlers for opening/closing popup
  const showPopup = useCallback(() => setIsOpen(true), []);
  const closePopup = useCallback(() => setIsOpen(false), []);

  const handleShowPopup = useCallback(
    (evt: React.MouseEvent) => {
      showPopup();
      evt.preventDefault();
    },
    [showPopup],
  );

  // Renders the trigger component
  const renderTrigger = useMemo(() => {
    return (
      <span
        ref={triggerRef}
        onClick={handleShowPopup}
        style={{
          cursor: 'pointer',
          flex: flex ? (typeof flex === 'number' ? flex : 1) : undefined,
          ...(absolute
            ? {
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              }
            : {}),
        }}>
        {trigger}
      </span>
    );
  }, [flex, handleShowPopup, absolute, trigger]);

  // Renders the inner component
  const renderChildren = useMemo(() => {
    let top: number | undefined = undefined;
    let left: number | undefined = undefined;

    if (isOpen && triggerRect) {
      const content = contentRef.current;

      if (content) {
        if (!contentRect) {
          // Get size of content
          setContentRect(content.children[0].getBoundingClientRect());
        } else {
          const screenBounds = {
            width: window.innerWidth,
            height: window.innerHeight,
          };

          // Top position should be calculated from the trigger element..
          const position = getPopupPosition(
            triggerRect,
            contentRect,
            screenBounds,
            theme.spacing['8'],
          );
          left = position.left;
          top = position.top;
        }
      }
    }

    return (
      <div
        ref={contentRef}
        className="di-div"
        style={{
          position: 'fixed',
          left,
          top,
          zIndex: 1000,
          ...(isOpen ? {display: undefined} : {display: 'none'}),
          ...(!contentRect ? {visibility: 'hidden'} : {}),
        }}>
        {children(closePopup)}
      </div>
    );
  }, [isOpen, triggerRect, contentRect, children, closePopup, theme.spacing]);

  const handleDismissIfClickedOutside = useCallback(
    (evt: MouseEvent) => {
      // Only close if outside content rect
      if (
        contentRef.current &&
        evt.target &&
        !isDescendant(contentRef.current, evt.target as HTMLElement)
      ) {
        closePopup();
      }
    },
    [closePopup],
  );

  // Handle dismissal of menu when clicking outside menu
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleDismissIfClickedOutside);
    } else {
      document.removeEventListener('click', handleDismissIfClickedOutside);
    }
    return () =>
      document.removeEventListener('click', handleDismissIfClickedOutside);
  }, [handleDismissIfClickedOutside, isOpen]);

  // Handle measure when open changes
  useEffect(() => {
    if (isOpen) {
      const trigger = triggerRef.current;
      if (trigger) {
        const triggerRect = trigger.children[0].getBoundingClientRect();
        setTriggerRect(triggerRect);
      }
    }
  }, [isOpen]);

  return (
    <>
      {renderTrigger}
      {renderChildren}
    </>
  );
};

function isDescendant(parent: HTMLElement, child: HTMLElement) {
  let el = child.parentElement;
  while (el != null) {
    if (el == parent) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}
